/**
 * RfhqProductGrid — websitehq's own product grid.
 *
 * Owns: the container, column widths, appending pages, subscribing to cart
 * state, and delegating the interactions core does not know about.
 *
 * Does NOT own: pricing, stock, filters, facets (raftor_webhq), nor cart and
 * wishlist binding (core webshop — this calls its binders, it does not
 * reimplement them).
 *
 * Grid view only. List view is dropped — see PRODUCT_CARD_PLAN.md §3.
 */

import { renderCard } from './card/render.js';
import { patchSlot } from './card/patch.js';
import { startCountdownTicker, stopCountdownTicker } from './card/countdown.js';
import { Paginator } from './paginator.js';
import { cartStore } from './cart_store.js';
import { LAYOUT, ACTION_KIND, STOCK_STATE } from './model.js';
import { createVariantResolver } from './variant_resolver.js';
import { adaptPrice } from './adapters/webshop.js';

function waConfig() {
	return (typeof window !== 'undefined' && window.WhatsAppSchema) || {};
}

/**
 * A default-selection resolve is 2-3 sequential frappe.call round trips
 * (attributes → next-attribute-and-values → stock check). Firing that for
 * every card on a page of 20+ at once floods the browser's per-origin
 * connection cap (~6 concurrent in most browsers) — found live, a request
 * queued behind that burst could take 30+ seconds to even start, which
 * looked identical to "stuck forever" until measured directly. This caps
 * how many of those chains run at once; the rest wait their turn rather
 * than all queuing inside the browser's own connection pool instead.
 */
const DEFAULT_SELECTION_CONCURRENCY = 4;
let _defaultSelectionActive = 0;
const _defaultSelectionQueue = [];

function runThrottled(task) {
	if (_defaultSelectionActive >= DEFAULT_SELECTION_CONCURRENCY) {
		_defaultSelectionQueue.push(task);
		return;
	}
	_defaultSelectionActive += 1;
	Promise.resolve()
		.then(task)
		.catch(() => {})
		.then(() => {
			_defaultSelectionActive -= 1;
			const next = _defaultSelectionQueue.shift();
			if (next) runThrottled(next);
		});
}

let _nearScreen = null;
function whenNearScreen(card, task) {
	if (typeof IntersectionObserver === 'undefined') { task(); return; }
	if (!_nearScreen) {
		_nearScreen = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				_nearScreen.unobserve(entry.target);
				const run = entry.target._rfhqWhenNear;
				entry.target._rfhqWhenNear = null;
				if (run) run();
			});
		}, { rootMargin: '600px 0px' });
	}
	card._rfhqWhenNear = task;
	_nearScreen.observe(card);
}

// Two across on a phone, as the shop's own listing is (webshop_grid_shim.js): a col-sm-* class alone has no effect below 576px, so a card would be one wide item per row.
const COLUMN_CLASS = { 2: 'col-6', 3: 'col-6 col-sm-4', 4: 'col-6 col-sm-3' };

export class RfhqProductGrid {
	/**
	 * @param {object} opts
	 * @param {HTMLElement} opts.el         container the cards are appended to
	 * @param {{fetch:Function}} opts.source
	 * @param {object} [opts.filters]
	 * @param {number} [opts.columns=3]
	 * @param {string} [opts.layout='standard']
	 * @param {boolean} [opts.infiniteScroll=false]
	 * @param {number} [opts.maxItems]      hard cap; blocks that never paginate
	 * @param {Function} [opts.wrapCard]    (cardEl) => el, for carousel slides
	 * @param {string} [opts.columnClass]   explicit override; '' for no column
	 * @param {boolean} [opts.claimOwnership=true]
	 *        Whether to stamp data-rfhq-owned. websitehq owns the VISUALS
	 *        everywhere; ownership here is the narrower claim that it also
	 *        draws the variant rows and qty stepper, which tells raftor_webhq
	 *        to stand off. On the catalog page we want the opposite: our
	 *        markup, webhq's interactive behaviour on top of it.
	 */
	constructor(opts) {
		this.el = opts.el;
		this.source = opts.source;
		this.columns = opts.columns || 3;
		this.layout = opts.layout || LAYOUT.STANDARD;
		this.infiniteScroll = Boolean(opts.infiniteScroll);
		this.maxItems = opts.maxItems || null;
		this.wrapCard = opts.wrapCard || null;
		this.onEmpty = opts.onEmpty || null;
		// Block-level override of the action kind; undefined leaves the
		// adapter's per-item decision (cart / explore / soldout) alone.
		this.actionKind = opts.actionKind || null;
		this.claimOwnership = opts.claimOwnership !== false;
		this._columnClassOverride = opts.columnClass;
		// Block-level "Show Price" toggle. A presentation concern, so it is a
		// render option — not a mutation of the model, which is what the old
		// suppressPrice() did by nulling the fetched price fields.
		this.showPrice = opts.showPrice !== false;
		this.settings = {};

		this._modelsById = new Map();
		this._unsubscribe = null;

		this.paginator = new Paginator({
			source: this.source,
			filters: opts.filters || {},
			onPage: (models, result) => this._append(models, result),
		});

		this._markOwned();
		this._applySiteThemeFallback();
		this._bindDelegates();
		this._subscribeCart();
	}

	/**
	 * Claim this container so raftor_webhq's two page-agnostic scripts
	 * (product_variant_swatches.js, listing_quantity.js) leave its cards alone —
	 * websitehq already renders colour dots and the qty stepper natively, and a
	 * second copy of either would be visible.
	 *
	 * Opt-out: `window.rfhqProductCardOwnership = false` (a Website Script will
	 * do) stops the marker being written and both apps fall straight back to the
	 * previous behaviour, with no deploy.
	 *
	 * Enabled now that raftor_webhq supplies a template-level "from" price
	 * (_fix_template_price_in_listing). Before that, taking ownership stripped
	 * price and Add to Cart from every card, because this catalog is entirely
	 * variant templates and only webhq's variant resolution was producing them.
	 */
	_markOwned() {
		if (!this.el) return;
		if (!this.claimOwnership) return;
		if (typeof window !== 'undefined' && window.rfhqProductCardOwnership === false) return;
		this.el.setAttribute('data-rfhq-owned', '1');
	}

	/** True when this grid claimed its cards, i.e. webhq is standing off them. */
	get owned() {
		return Boolean(this.el && this.el.hasAttribute('data-rfhq-owned'));
	}

	/**
	 * Independent of claimOwnership/data-rfhq-owned above — this is about
	 * colour, not interaction, and websitehq owns the colour on every grid
	 * it draws, catalog takeover included.
	 *
	 * Delegates to rfhq_owned_theme.js's shared implementation (originally
	 * lived here only; extracted so every other data-rfhq-owned surface —
	 * order confirmation, order detail, wishlist, none of them a
	 * RfhqProductGrid — gets the identical fallback instead of each needing
	 * its own copy). See that file for the full reasoning: a page-builder
	 * Item Grid/Carousel already sits inside its RFHQ Section's own
	 * data-rfhq-theme and must keep winning; only a grid with no themed
	 * ancestor at all (the catalog takeover, PDP/cart recommendation rails)
	 * falls back to window.rfhqSiteTheme.
	 */
	_applySiteThemeFallback() {
		if (typeof window !== 'undefined' && window.rfhqApplyOwnedThemeFallback) {
			window.rfhqApplyOwnedThemeFallback(this.el);
		}
	}

	get columnClass() {
		if (this._columnClassOverride !== undefined) return this._columnClassOverride;
		return COLUMN_CLASS[this.columns] || COLUMN_CLASS[3];
	}

	get renderCtx() {
		return {
			layout: this.layout,
			columnClass: this.columnClass,
			settings: Object.assign(
				{
					// Admin-configurable via Website HQ Settings (size_picker_style),
					// injected on every page by that doctype's own head_html hook -
					// same pattern as WhatsAppSchema/waConfig() below. Read off
					// `window` directly rather than `this.settings` because that
					// object is wholesale replaced by every listing fetch (see
					// `load`/`loadMore` below) and would silently drop this.
					sizePickerStyle: (typeof window !== 'undefined' && window.rfhqSizePickerStyle) || 'Buttons',
					// The word a price of 0 reads (Website HQ Settings, Word for a Price of 0); blank: a card shows no price for it, as before.
					freePriceWord: (typeof window !== 'undefined' && window.rfhqFreePriceWord) || '',
				},
				this.settings
			),
			showPrice: this.showPrice,
			// Variant rows are ours to draw only when webhq is standing off;
			// otherwise it draws them and two sets would appear.
			owned: this.owned,
			actionKind: this.actionKind,
			// Already injected on every page by update_website_context for the
			// floating widget; reused rather than re-plumbed. Read here so the
			// renderer itself stays free of globals.
			whatsappNumber: waConfig().number,
			whatsappMessage: waConfig().message,
		};
	}

	/** First page. */
	async load() {
		cartStore.listen();
		cartStore.refresh();

		const result = await this.paginator.loadMore();

		// Nothing matched. A hand-picked item can be unpublished or outside the
		// caller's permitted brands, so this is a legitimate outcome, not an
		// error — let the block decide whether to hide itself.
		if (!result || !result.models.length) {
			if (this.onEmpty) this.onEmpty();
			return;
		}

		if (this.maxItems) {
			// The server's page size (Webshop Settings > Products Per Page) and
			// this block's requested count are unrelated numbers — the first
			// fetch may well be short of maxItems. Keep asking rather than
			// silently showing fewer items than configured.
			await this.paginator.loadUntil(this.maxItems);
		} else if (this.infiniteScroll) {
			this.paginator.attachScroll();
		}
	}

	/**
	 * Render a first page that someone else fetched, then continue from it.
	 *
	 * Used by the webshop grid shim: core's ProductView already has page one in
	 * hand, so re-requesting it would double the work and could even disagree
	 * with what the rest of the page is showing.
	 */
	async seed(rawItems, settings, pagination) {
		cartStore.listen();
		cartStore.refresh();

		const models = await this.source.adaptRaw(rawItems, settings);
		if (!models.length) {
			if (this.onEmpty) this.onEmpty();
			return;
		}

		this.settings = settings || {};
		this._append(models, { settings: this.settings });
		// Optimistic: core does not pass the pagination envelope down to the
		// grid, so assume more exist and let the first real fetch return the
		// authoritative has_more.
		this.paginator.seed(
			models,
			Object.assign({ hasMore: true, nextStart: models.length }, pagination || {})
		);

		if (this.infiniteScroll) this.paginator.attachScroll();
	}

	/** Context change — filters, sort, search. Explicit, never sniffed. */
	async reset(filters) {
		this.paginator.detachScroll();
		this.paginator.reset(filters);
		this._modelsById.clear();
		if (this.el) this.el.innerHTML = '';
		await this.load();
	}

	_append(models, result) {
		if (!this.el) return;

		if (result && result.settings) this.settings = result.settings;

		let toRender = models;
		if (this.maxItems) {
			const room = this.maxItems - this._modelsById.size;
			if (room <= 0) {
				this.paginator.hasMore = false;
				return;
			}
			toRender = models.slice(0, room);
		}

		const frag = document.createDocumentFragment();
		const needsDefaultSelection = [];
		for (const model of toRender) {
			this._modelsById.set(model.id, model);
			const card = renderCard(model, this.renderCtx);
			// Swatch/size clicks are handled by ONE page-wide delegated listener
			// (_bindDelegates, bound once by whichever grid instance is first to
			// construct), not by `this` — a page can have several grid instances
			// (a listing plus a carousel, say), each with its own _modelsById, so
			// the listener needs to find the RIGHT instance per card rather than
			// always resolving against whichever instance happened to bind it.
			RfhqProductGrid._cardOwners = RfhqProductGrid._cardOwners || new WeakMap();
			RfhqProductGrid._cardOwners.set(card, this);
			if (model.swatches.length || model.sizes.length) needsDefaultSelection.push(card);
			frag.appendChild(this.wrapCard ? this.wrapCard(card) : card);
		}
		this.el.appendChild(frag);

		if (this.maxItems && this._modelsById.size >= this.maxItems) {
			this.paginator.hasMore = false;
		}

		// One page-wide ticker, started only once a card that needs it exists.
		startCountdownTicker();

		// A card works out its default choice only when it is near the screen (a page of cards that are far below would otherwise queue a chain of server calls each, and the ones in view would wait
		// behind them); without the browser's observer every card does, as before.
		needsDefaultSelection.forEach((card) => whenNearScreen(card, () => runThrottled(() => this._applyDefaultSelection(card))));

		this._afterAppend(toRender.length);
	}

	/**
	 * Parity with the PDP's own applyDefaultPreselection() — a card with
	 * pickable options shouldn't sit on "Explore" until the shopper clicks
	 * first; it resolves a default combination (first value per attribute)
	 * the moment it renders, same as the reference implementation. Queued
	 * through runThrottled by the caller, not fire-and-forget — the
	 * returned promise is what lets that queue know this card is done and
	 * move on to the next.
	 */
	_applyDefaultSelection(card) {
		RfhqProductGrid._resolvers = RfhqProductGrid._resolvers || new WeakMap();
		let resolver = RfhqProductGrid._resolvers.get(card);
		if (!resolver) {
			resolver = createVariantResolver(card.dataset.itemCode);
			RfhqProductGrid._resolvers.set(card, resolver);
		}

		return resolver.selectDefault().then((result) => {
			if (this.renderCtx.settings.sizePickerStyle === 'Dropdown') {
				this._maybeLoadSizeStock(card, resolver, result.selection || {});
			}

			// Every value selectDefault() actually tried and proved out of
			// stock gets greyed out here regardless of the overall outcome -
			// a shopper looking at 5 identical, clickable-looking pills on a
			// card that's stuck on "Explore" has no way to tell "sold out
			// everywhere" from "just hasn't picked yet", the exact gap a
			// manual click through grid.js's own delegated handler already
			// closes for itself (that path adds .variant-pill-oos the moment
			// a click resolves out of stock - this is the same treatment for
			// attempts the auto-pick made on the shopper's behalf, not a new
			// visual language). Applied even when the loop DID land on an
			// in-stock item, since earlier attempts in the same run can still
			// have been dead ends worth greying out.
			(result.outOfStockAttempts || []).forEach(({ attribute, value }) => {
				const pill = card.querySelector(
					`.variant-pill[data-attribute="${cssEscape(attribute)}"][data-value="${cssEscape(value)}"]`
				);
				if (pill) {
					pill.classList.add('variant-pill-oos');
					pill.setAttribute('aria-pressed', 'false');
				}
			});

			// Greyed-out pills alone still make a shopper hunt across every
			// size to work out "sold out" for themselves. adaptStock()
			// deliberately suppresses the stock line for any has_variants
			// template (a partly-in-stock template genuinely has nothing
			// truthful to say in one line) - but result.soldOutEverywhere is
			// the one case that restriction wasn't meant to cover: every
			// value of the template's own first attribute was tried and
			// every one came back out of stock, so "Out of stock" is no
			// longer a simplification, it's just true. Patches the existing
			// stock slot the same way price/actions already do, rather than
			// inventing a second place this text could come from.
			//
			// actions.kind flips to SOLDOUT alongside it - card/action/
			// soldout.js renders nothing at all ("there is no purchase path
			// at all, so the actions slot stays empty and the stock line
			// carries the message"), the exact contract a non-variant
			// out-of-stock item already gets. Leaving "Explore" live here
			// would have meant a template that's sold out in every size and
			// a plain item that's sold out say the same thing but only one
			// of them still offers a button - the same "same fact, different
			// treatment depending on which code path produced it"
			// inconsistency this whole card system exists to avoid.
			if (result.soldOutEverywhere) {
				const id = card.dataset.cardId;
				const model = id && this._modelsById.get(id);
				if (model) {
					const next = Object.assign({}, model, {
						stock: { state: STOCK_STATE.OUT, label: 'Out of stock', qty: 0 },
						actions: { kind: ACTION_KIND.SOLDOUT },
					});
					this._modelsById.set(id, next);
					patchSlot(card, 'stock', next, this.renderCtx);
					patchSlot(card, 'actions', next, this.renderCtx);
				}
				return;
			}

			// A card whose default doesn't land on a real, IN-STOCK item —
			// 'incomplete' (no combination resolver.selectDefault() tried
			// exists), or 'resolved' but out of stock — stays on "Explore"
			// rather than showing pills marked active for a selection that
			// isn't actually purchasable; that read as "I picked something
			// and nothing happened" before this check existed.
			if (result.status !== 'resolved' || !result.inStock) return;
			// A click that landed while the default was still resolving wins —
			// don't stomp a real choice with the auto-pick.
			if (card.querySelector('.variant-pill.active')) return;

			Object.keys(result.selection || {}).forEach((attribute) => {
				const value = result.selection[attribute];
				const pill = card.querySelector(
					`.variant-pill[data-attribute="${cssEscape(attribute)}"][data-value="${cssEscape(value)}"]`
				);
				if (pill) {
					pill.classList.add('active');
					pill.setAttribute('aria-pressed', 'true');
				}
			});

			this._onVariantResolved(card, result.itemCode);
		});
	}

	/**
	 * Dropdown presentation only (ctx.settings.sizePickerStyle === 'Dropdown').
	 * Bulk, stock-checked fetch of every in-stock value of the card's
	 * size-dropdown attribute for `selection`'s resolved colour — one round
	 * trip via variant_resolver.js's getStockedValues(), not one per
	 * candidate size (see DEFAULT_SELECTION_CONCURRENCY's own comment on why
	 * that N-call pattern is deliberately avoided). Called both from the
	 * default-selection flow above and from the manual colour click handler
	 * below, guarded by `resolvedForColour` so a repeat call for the SAME
	 * colour is a no-op but a genuine colour change re-checks stock rather
	 * than leaving the dropdown showing the previous colour's sizes.
	 */
	_maybeLoadSizeStock(card, resolver, selection) {
		const id = card.dataset.cardId;
		const model = id && this._modelsById.get(id);
		if (!model || !model.sizeDropdown || !model.sizeDropdown.attribute) return;

		// Found live: on templates where the swatch attribute isn't actually
		// stored on the variant (an "optional" attribute for uniqueness, or
		// simply never set per-variant on some SKUs) - `Colour` shows swatches
		// for display, but a variant is uniquely identified by Size alone -
		// core's own get_next_attribute_and_values resolves a full match the
		// moment Size is picked, WITHOUT ever adding Colour to the selection.
		// `selection[colourAttribute]` is legitimately never populated for
		// those templates, and this method is only ever called from a
		// SETTLED selectDefault()/select() result, so there is nothing left
		// to wait for - treating "no colour" as "colour doesn't constrain
		// this template's sizes" (empty filter -> every variant) is correct,
		// not a stand-in for "not resolved yet". Requiring a colour value
		// before proceeding left the dropdown on "Loading sizes…" forever for
		// every such card, since nothing ever re-triggers this call on its own.
		const colourAttribute = model.swatches[0] && model.swatches[0].attribute;
		const colourValue = colourAttribute ? selection[colourAttribute] : undefined;

		const sizeAttr = model.sizeDropdown.attribute;
		const colourKey = colourValue || '__none__';
		if (model.sizeDropdown.resolvedForColour === colourKey) return; // already done for this colour (or lack of one)

		resolver
			.getStockedValues(sizeAttr, colourValue ? { [colourAttribute]: colourValue } : {})
			.then((values) => {
				if (values === null) return; // call failed — stay on loading state, don't claim sold out
				const current = this._modelsById.get(id);
				if (!current) return;
				const next = Object.assign({}, current, {
					sizeDropdown: Object.assign({}, current.sizeDropdown, {
						options: values.map((v) => ({ value: v, label: v })),
						selected: selection[sizeAttr] || null,
						resolvedForColour: colourKey,
					}),
				});
				this._modelsById.set(id, next);
				patchSlot(card, 'sizeDropdown', next, this.renderCtx);

				if (!values.length) {
					// Same "sold out everywhere" treatment _applyDefaultSelection
					// already applies above — one place this fact is expressed,
					// not a second empty-state UI.
					const soldOut = Object.assign({}, next, {
						stock: { state: STOCK_STATE.OUT, label: 'Out of stock', qty: 0 },
						actions: { kind: ACTION_KIND.SOLDOUT },
					});
					this._modelsById.set(id, soldOut);
					patchSlot(card, 'stock', soldOut, this.renderCtx);
					patchSlot(card, 'actions', soldOut, this.renderCtx);
				}
			});
	}

	/**
	 * Obligations after any append. These were spread across infinite_scroll.js
	 * and must not be lost: core's binders only attach to elements that existed
	 * when they last ran, and `products-loaded` is the page's public signal that
	 * new cards exist.
	 */
	_afterAppend(count) {
		// Bind ONCE per page, not per append. Both binders are delegated
		// `$('.page_content').on('click', ...)` with no teardown, so calling
		// them again per page stacks a fresh handler each time — N pages
		// scrolled meant N `update_cart` round-trips per click. (The card
		// markup is delegated-to, not bound directly, so cards appended later
		// are still covered by the first bind.)
		if (typeof webshop !== 'undefined' && !RfhqProductGrid._bindersAttached) {
			// webshop.provide()'s real namespace is webshop.webshop.shopping_cart
			// (shopping_cart.js line 5) - webshop.shopping_cart (one level up) is
			// always undefined, so this line silently never fired. Cart buttons
			// still worked on /all-products only because webshop's own
			// www/all-products/index.js binds correctly-namespaced, independently
			// of this grid. Any grid-rendered surface without its own page script
			// (PDP/cart recommendation rails - this file is their only chance to
			// bind) was relying on dead code.
			const cart = webshop.webshop && webshop.webshop.shopping_cart && webshop.webshop.shopping_cart.bind_add_to_cart_action;
			const wish =
				webshop.webshop && webshop.webshop.wishlist && webshop.webshop.wishlist.bind_wishlist_action;

			if (cart) webshop.webshop.shopping_cart.bind_add_to_cart_action();
			if (wish) webshop.webshop.wishlist.bind_wishlist_action();
			// Only latch once something actually bound — otherwise an early
			// append before webshop loads would permanently suppress binding.
			if (cart || wish) RfhqProductGrid._bindersAttached = true;
		}

		this._syncTrialRoomState();

		if (typeof document !== 'undefined') {
			document.dispatchEvent(new CustomEvent('products-loaded', { detail: { count } }));
		}
		if (typeof window !== 'undefined' && typeof window.catalogExtensionsOnProductsLoaded === 'function') {
			try {
				window.catalogExtensionsOnProductsLoaded({ count });
			} catch (err) {
				/* an extension hook must not break the listing */
			}
		}
	}

	/**
	 * Marks a card's "Try In Trial Room" toggle (root.js) as already-added
	 * on load, so revisiting a page you already shortlisted an item from
	 * doesn't show the icon as if it were still un-added. Membership is
	 * fetched once per page (cached on the class, mirroring
	 * _bindersAttached above) and re-applied on every append, since
	 * pagination/carousels add cards after this first runs.
	 */
	_syncTrialRoomState() {
		if (typeof window === 'undefined' || !window.rfhqTrialRoomEnabled) return;
		if (typeof document === 'undefined') return;
		// A guest can never have a Trial Room (nothing to be session-scoped
		// to) - get_trial_room_item_codes correctly rejects the call
		// server-side, but frappe.call's own default error handling shows
		// that rejection as a "Method Not Allowed" dialog on every guest
		// page load *before* this call's own graceful error:()=>resolve([])
		// ever runs - skip the call entirely rather than try to suppress
		// Frappe's own dialog after the fact.
		if (typeof frappe !== 'undefined' && frappe.session && frappe.session.user === 'Guest') return;

		if (!RfhqProductGrid._trialRoomCodesPromise) {
			RfhqProductGrid._trialRoomCodesPromise = new Promise((resolve) => {
				frappe.call({
					method: 'raftor_webhq.catalog.trial_room.get_trial_room_item_codes',
					callback: (r) => resolve((r && r.message) || []),
					error: () => resolve([]),
				});
			});
		}

		RfhqProductGrid._trialRoomCodesPromise.then((codes) => {
			if (!codes.length) return;
			const codeSet = new Set(codes);
			document.querySelectorAll('.rfhq-pc-trialroom-toggle[data-item-code]').forEach((btn) => {
				if (!codeSet.has(btn.dataset.itemCode)) return;
				btn.classList.add('rfhq-pc-trialroom-toggle--added');
				btn.setAttribute('aria-label', 'Added to Trial Room');
				btn.setAttribute('title', 'Added to Trial Room');
			});
		});
	}

	/* ── cart state → re-render, never DOM mutation ────────────────────── */

	_subscribeCart() {
		this._unsubscribe = cartStore.subscribe((changedCodes) => {
			for (const code of changedCodes || []) {
				this._updateCartState(code);
			}
		});
	}

	/**
	 * The card is redrawn by the RENDERER from an updated model — but only the
	 * slot that changed. A full replace would tear down the zoom magnifier's
	 * per-container state mid-hover, drop keyboard focus from the stepper, and
	 * re-flash the image. See card/patch.js.
	 */
	_updateCartState(itemCode) {
		if (!this.el || !itemCode) return;

		for (const [id, model] of this._modelsById) {
			if (model.itemCode !== itemCode) continue;

			const qty = cartStore.getQty(itemCode);
			if (model.state.cartQty === qty) continue;

			const next = Object.assign({}, model, {
				state: Object.assign({}, model.state, { cartQty: qty, inCart: qty > 0 }),
			});
			this._modelsById.set(id, next);

			const existing = this.el.querySelector(`[data-card-id="${cssEscape(id)}"]`);
			if (!existing) continue;

			// Cart quantity shows up in two places: the action row (button ⇄
			// stepper) and the floating indicator, which lives in the title row.
			// The indicator is cheap to update in place and has no interactive
			// state to preserve.
			const patched = patchSlot(existing, 'actions', next, this.renderCtx);
			if (!patched) {
				// No actions slot on this layout — fall back to a full redraw
				// rather than silently leaving stale state on screen.
				existing.parentNode.replaceChild(renderCard(next, this.renderCtx), existing);
				continue;
			}

			const indicator = existing.querySelector('.cart-indicator');
			if (indicator) {
				indicator.textContent = String(next.state.cartQty || 1);
				indicator.classList.toggle('hidden', !next.state.inCart);
			}
		}
	}

	/**
	 * A card's swatch/size picker fully resolved to a buyable variant —
	 * swap its actions slot from "explore" (pick options) to a real
	 * cart.js Add to Cart button carrying the RESOLVED item_code, the same
	 * targeted patchSlot pattern _updateCartState uses above. Model
	 * identity stays the route (`id` — INVARIANT 1, model.js); only
	 * `itemCode`/`actions` change.
	 *
	 * The click that adds it to the cart needs no new code here: core's
	 * bind_add_to_cart_action() is a page-wide delegated binder already
	 * attached in _afterAppend, so it picks up this freshly-patched
	 * .btn-add-to-cart-list[data-item-code] the same as any other.
	 */
	_onVariantResolved(cardEl, resolvedItemCode) {
		if (!cardEl || !resolvedItemCode) return;

		const id = cardEl.dataset.cardId;
		const model = id && this._modelsById.get(id);
		if (!model) return;

		const next = Object.assign({}, model, {
			itemCode: resolvedItemCode,
			actions: {
				kind: ACTION_KIND.CART,
				addLabel: this.settings.enable_checkout ? 'Add to Cart' : 'Add to Quote',
				gotoLabel: this.settings.enable_checkout ? 'Go to Cart' : 'Go to Quote',
				gotoHref: '/cart',
			},
			state: Object.assign({}, model.state, { cartQty: cartStore.getQty(resolvedItemCode) }),
		});
		this._modelsById.set(id, next);

		patchSlot(cardEl, 'actions', next, this.renderCtx);

		// A resolved variant's item_code differs from the template code the
		// card rendered with (root.js's own data-item-code never changes) —
		// this is the one place other code (the Trial Room toggle's own
		// delegate in _bindDelegates below) can find it instead of falling
		// back to a template code that can't actually be added to the cart.
		cardEl.dataset.resolvedItemCode = resolvedItemCode;

		this._refreshResolvedPrice(cardEl, id, resolvedItemCode);
	}

	/**
	 * A resolved variant can (and here, does) carry its own price, distinct
	 * from the template's cheapest-variant "From ₹X" the card rendered with -
	 * left unpatched, the price slot silently kept showing "From" and the
	 * template's rate forever, regardless of which size/colour the shopper
	 * actually picked (found live: the CTA correctly targeted the resolved
	 * item_code while the price two lines above it never moved). adaptPrice
	 * is the exact same webshop→model translation adaptItem already uses for
	 * the initial render - reused rather than re-implemented so a real price
	 * difference between variants shows up here too, not just a cosmetic
	 * "From" removal. skip_quotation_creation:1 matches product_variant_
	 * swatches.js's own call to this endpoint - without it, every size click
	 * would silently create/touch a cart quotation just to read a price.
	 */
	/**
	 * Swap the card's own photo to the resolved variant's image (mirrors
	 * raftor_webhq's pdp_variant_swatches.js). Mutates the existing <img>'s
	 * src directly rather than going through patchSlot/renderMedia:
	 * image_zoom_hover.js binds this exact <img> node once per card (a
	 * closure plus a MutationObserver on its `src` attribute) and marks the
	 * container `data-zoom-initialized` so it is never rebound - replacing
	 * the node via a slot re-render would silently break hover-zoom for any
	 * card already initialized by the time a swatch is clicked. Setting
	 * `src` in place is also what that MutationObserver is already watching
	 * for, so the zoom window's own image updates for free.
	 */
	_updateResolvedMedia(cardEl, id, imageUrl) {
		if (!imageUrl) return;

		const img = cardEl.querySelector('.card-img-container a img.card-img');
		if (img && img.getAttribute('src') !== imageUrl) {
			img.setAttribute('src', imageUrl);
		}

		const quickview = cardEl.querySelector('.rfhq-pc-quickview');
		if (quickview) quickview.dataset.rfhqQuickview = imageUrl;

		const model = this._modelsById.get(id);
		if (model && model.media.src !== imageUrl) {
			this._modelsById.set(
				id,
				Object.assign({}, model, { media: Object.assign({}, model.media, { src: imageUrl }) })
			);
		}
	}

	_refreshResolvedPrice(cardEl, id, resolvedItemCode) {
		if (typeof frappe === 'undefined' || !frappe.call) return;

		frappe.call({
			method: 'raftor_webhq.catalog.api.get_product_info',
			args: { item_code: resolvedItemCode, skip_quotation_creation: 1 },
			callback: (r) => {
				// Stale by the time it lands (another size picked meanwhile,
				// or the card left the page) - the resolved item_code stamped
				// in _onVariantResolved is the one source of truth for "is
				// this response still relevant".
				if (cardEl.dataset.resolvedItemCode !== resolvedItemCode) return;

				const info = r && r.message && r.message.product_info;
				if (!info) return;

				this._updateResolvedMedia(cardEl, id, info.image);

				if (!info.price) return;

				const model = this._modelsById.get(id);
				if (!model) return;

				const next = Object.assign({}, model, { price: adaptPrice(info.price) });
				this._modelsById.set(id, next);
				patchSlot(cardEl, 'price', next, this.renderCtx);
			},
		});
	}

	/* ── interaction ───────────────────────────────────────────────────────
	 * Only what core does not already handle. The Add to Cart button is core's
	 * (bind_add_to_cart_action) — handling it here too would double-add.
	 */

	_bindDelegates() {
		if (typeof document === 'undefined' || RfhqProductGrid._delegatesBound) return;
		RfhqProductGrid._delegatesBound = true;

		document.addEventListener('click', (e) => {
			const stepBtn = e.target.closest('.listing-qty-wrapper .number-spinner button[data-dir]');
			if (stepBtn) {
				const wrapper = stepBtn.closest('.listing-qty-wrapper');
				const itemCode = wrapper && wrapper.dataset.itemCode;
				if (!itemCode) return;
				const delta = stepBtn.dataset.dir === 'up' ? 1 : -1;
				cartStore.setQty(itemCode, cartStore.getQty(itemCode) + delta);
				return;
			}

			// Core adds the item server-side; optimisticAdd() flips this
			// card's actions slot to the stepper immediately rather than
			// leaving the button inert for the add's own round trip plus a
			// second network call (found live -- that gap read as "nothing
			// happened" on a click). No manual delay needed to reconcile it
			// afterward -- cart_store.js's listen() calls refresh() itself
			// the moment core's own update_cart request actually completes
			// (a real ajaxComplete signal, not a guessed wait).
			const addBtn = e.target.closest('.btn-add-to-cart-list[data-item-code]');
			if (addBtn && !addBtn.classList.contains('go-to-cart-grid')) {
				cartStore.optimisticAdd(addBtn.dataset.itemCode, 1);
				return;
			}

			// A typed quantity that has not reached the server must be flushed
			// before we navigate, or the shopper's edit is silently lost.
			const cartLink = e.target.closest('.listing-go-to-cart');
			if (cartLink) {
				const wrapper = cartLink.closest('.listing-qty-wrapper');
				const itemCode = wrapper && wrapper.dataset.itemCode;
				if (itemCode && cartStore.isPending(itemCode)) {
					e.preventDefault();
					const href = cartLink.getAttribute('href') || '/cart';
					cartStore.flush(itemCode).then(() => {
						window.location.href = href;
					});
				}
			}
		});

		// Swatch / size picker — resolves a template + selected attributes
		// (variant_resolver.js) into a specific, stock-checked item_code,
		// then hands off to _onVariantResolved to swap the actions slot.
		// One listener for every grid instance on the page (same reason as
		// the block above); RfhqProductGrid._cardOwners (populated in
		// _append) is what lets it find the RIGHT instance's _modelsById
		// for whichever card was actually clicked.
		document.addEventListener('click', (e) => {
			const pill = e.target.closest('.variant-pill[data-attribute][data-value]');
			if (
				!pill ||
				pill.disabled ||
				pill.classList.contains('variant-pill-oos') ||
				pill.classList.contains('variant-pill-disabled')
			) {
				return;
			}

			const card = pill.closest('.item-card');
			const grid = card && RfhqProductGrid._cardOwners && RfhqProductGrid._cardOwners.get(card);
			if (!grid) return;

			const attribute = pill.getAttribute('data-attribute');
			const value = pill.getAttribute('data-value');

			// Only one value per attribute row can be selected — mirror the
			// click immediately, before the network round-trip, so the UI
			// never feels laggy even though resolution is async. `.active`
			// is webhq_overrides.css's own existing selected-state class for
			// this exact `.variant-pill` contract (shared, not duplicated —
			// see _shared.js's own comment on why the markup reuses it);
			// aria-pressed alongside it is this button's own semantic state.
			pill.closest('.variant-attr-row').querySelectorAll('.variant-pill').forEach((p) => {
				p.classList.toggle('active', p === pill);
				p.setAttribute('aria-pressed', String(p === pill));
			});

			RfhqProductGrid._resolvers = RfhqProductGrid._resolvers || new WeakMap();
			let resolver = RfhqProductGrid._resolvers.get(card);
			if (!resolver) {
				// The template code, not model.itemCode — root.js's own
				// data-item-code is set once at first render and (correctly)
				// never mutated, so it stays the template even after this
				// card has already resolved a variant once before.
				resolver = createVariantResolver(card.dataset.itemCode);
				RfhqProductGrid._resolvers.set(card, resolver);
			}

			resolver.select(attribute, value).then((result) => {
				if (result.status === 'stale' || result.status === 'no-match') return;

				if (result.status === 'incomplete') {
					const valid = result.validOptionsForAttributes || {};
					card.querySelectorAll('.variant-pill[data-attribute]').forEach((p) => {
						const attr = p.getAttribute('data-attribute');
						if (attr === attribute || p.getAttribute('aria-pressed') === 'true') return;
						const validValues = valid[attr];
						const isValid = !validValues || validValues.indexOf(p.getAttribute('data-value')) !== -1;
						p.classList.toggle('variant-pill-disabled', !isValid);
					});
					return;
				}

				// status === 'resolved'
				if (!result.inStock) {
					pill.classList.remove('active');
					pill.setAttribute('aria-pressed', 'false');
					pill.classList.add('variant-pill-oos');
					return;
				}

				// A manual colour re-pick must re-check size stock too, or the
				// dropdown keeps showing the PREVIOUS colour's in-stock sizes —
				// _maybeLoadSizeStock's own resolvedForColour guard makes this a
				// no-op for a size-pill click (or any repeat click of the same
				// colour), so it's safe to call unconditionally on the colour
				// attribute here.
				if (grid.renderCtx.settings.sizePickerStyle === 'Dropdown') {
					const id = card.dataset.cardId;
					const model = id && grid._modelsById.get(id);
					const colourAttribute = model && model.swatches[0] && model.swatches[0].attribute;
					if (colourAttribute && attribute === colourAttribute) {
						// select()'s own resolved result carries no `selection` field
						// (only selectDefault()'s does) — the resolver's internal
						// selection is already updated synchronously by the time
						// this callback runs, so read it back directly.
						grid._maybeLoadSizeStock(card, resolver, resolver.getSelection());
					}
				}

				grid._onVariantResolved(card, result.itemCode);
			});
		});

		// Media toolbar (wishlist/quickview) reveal on touch -- there is no
		// hover gesture to drive .rfhq-pc-media-toolbar's CSS :hover rule, so
		// the first tap on a card's image reveals it (via the --revealed
		// class, see rfhq_product_card.css) instead of navigating straight to
		// the PDP; a second tap, or a tap on an icon inside the toolbar
		// itself, is left to act normally. Click-outside-to-close mirrors
		// webshop's own ProductSearch dropdown idiom rather than inventing a
		// new one. Guarded on `(hover: none)` so a mouse/trackpad user (who
		// already gets the CSS :hover reveal) never has their first real
		// click on a card eaten by this.
		document.addEventListener('click', (e) => {
			if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;

			// A tap on the toolbar itself (an icon) always acts normally --
			// those buttons already preventDefault/stopPropagation their own
			// click where needed (quickview above, core's wishlist binder).
			if (e.target.closest('.rfhq-pc-media-toolbar')) return;

			const container = e.target.closest('.card-img-container');
			const toolbar = container && container.querySelector('.rfhq-pc-media-toolbar');
			const link = e.target.closest('.card-img-container a');

			if (link && toolbar && !toolbar.classList.contains('rfhq-pc-media-toolbar--revealed')) {
				e.preventDefault();
				document.querySelectorAll('.rfhq-pc-media-toolbar--revealed').forEach((el) => {
					if (el !== toolbar) el.classList.remove('rfhq-pc-media-toolbar--revealed');
				});
				toolbar.classList.add('rfhq-pc-media-toolbar--revealed');
				return;
			}

			if (!container) {
				document.querySelectorAll('.rfhq-pc-media-toolbar--revealed').forEach((el) => {
					el.classList.remove('rfhq-pc-media-toolbar--revealed');
				});
			}
		});

		// Quick view → websitehq's existing lightbox. Built once in
		// image_zoom_loader.js and already used by embed_card; the card only
		// supplies a trigger.
		document.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-rfhq-quickview]');
			if (!btn) return;
			e.preventDefault();
			// The card image is a link; stop the click reaching it.
			e.stopPropagation();
			if (!window.rfhqImageLightbox) return;
			window.rfhqImageLightbox.open(
				btn.dataset.rfhqQuickview,
				btn.dataset.rfhqQuickviewCaption,
				btn.dataset.rfhqQuickviewCaption,
				btn // focus returns here on close
			);
		});

		// Trial Room toggle - fire-and-forget, mirrors Wishlist's own
		// add-only-from-the-grid precedent (removal happens on /trial-room
		// itself, see trial_room_page.js). No per-card membership fetch on
		// initial render, so no "already added" state to sync here.
		document.addEventListener('click', (e) => {
			const trialBtn = e.target.closest('.rfhq-pc-trialroom-toggle[data-item-code]');
			if (!trialBtn) return;
			e.preventDefault();
			e.stopPropagation();
			if (trialBtn.classList.contains('rfhq-pc-trialroom-toggle--added')) return;

			// trialBtn.dataset.itemCode is the template code (root.js's own
			// data-item-code never changes after render). Prefer a resolved
			// variant code if the shopper already picked size/colour on this
			// card, so the shortlisted item is addable to cart directly
			// instead of always landing on Trial Room's "Select Size"
			// fallback. `data-resolved-item-code` is _onVariantResolved's own
			// marker (variant_resolver.js's picker, every card now — see
			// card/layout/_shared.js); `.variant-add-btn` is raftor_webhq's
			// own product_variant_swatches.js, kept as a fallback for any
			// card that script still decorates on its own (a page that never
			// touches RfhqProductGrid at all).
			const card = trialBtn.closest('.item-card');
			const resolvedBtn = card && card.querySelector('.variant-add-btn[data-item-code]:not(.variant-pending)');
			const resolvedCode = (card && card.dataset.resolvedItemCode) || (resolvedBtn && resolvedBtn.dataset.itemCode);
			const itemCode = resolvedCode || trialBtn.dataset.itemCode;

			frappe.call({
				method: 'raftor_webhq.catalog.trial_room.add_to_trial_room',
				args: { item_code: itemCode },
				callback: () => {
					trialBtn.classList.add('rfhq-pc-trialroom-toggle--added');
					trialBtn.setAttribute('aria-label', 'Added to Trial Room');
					trialBtn.setAttribute('title', 'Added to Trial Room');
					if (window.rfhqUpdateTrialRoomBadge) window.rfhqUpdateTrialRoomBadge(true);
				},
			});
		});

		document.addEventListener('change', (e) => {
			const input = e.target.closest('.listing-qty-wrapper .cart-qty');
			if (!input) return;
			const wrapper = input.closest('.listing-qty-wrapper');
			const itemCode = wrapper && wrapper.dataset.itemCode;
			if (!itemCode) return;
			const qty = parseInt(input.value, 10);
			cartStore.setQty(itemCode, isNaN(qty) || qty < 0 ? 0 : qty);
		});

		// Size dropdown (sizePickerStyle === 'Dropdown') — same resolve →
		// _onVariantResolved path the button pill click above uses, so
		// add-to-cart/price behave identically regardless of presentation.
		document.addEventListener('change', (e) => {
			const select = e.target.closest('.variant-size-select[data-attribute]');
			if (!select) return;
			const card = select.closest('.item-card');
			const grid = card && RfhqProductGrid._cardOwners && RfhqProductGrid._cardOwners.get(card);
			if (!grid || !select.value) return;

			RfhqProductGrid._resolvers = RfhqProductGrid._resolvers || new WeakMap();
			let resolver = RfhqProductGrid._resolvers.get(card);
			if (!resolver) {
				resolver = createVariantResolver(card.dataset.itemCode);
				RfhqProductGrid._resolvers.set(card, resolver);
			}

			resolver.select(select.getAttribute('data-attribute'), select.value).then((result) => {
				if (result.status !== 'resolved') return;
				// Only in-stock values are ever listed in the dropdown, so
				// !inStock should be unreachable in steady state — the one gap
				// is a genuine stock change landing between the bulk check and
				// this click, a narrow real race. Handled defensively: the
				// native <select> already shows the shopper's pick, so just
				// skip _onVariantResolved rather than push a known-OOS
				// item_code into the cart/price patch.
				if (!result.inStock) return;
				grid._onVariantResolved(card, result.itemCode);
			});
		});

		this._bindHoverSlideshow();
	}

	/**
	 * Cycle a card's stacked gallery frames (card/layout/_shared.js's
	 * renderMedia, backed by raftor_webhq's get_item_media) while the image
	 * is hovered or keyboard-focused, instead of the old single always-on
	 * second-image swap.
	 *
	 * mouseover/mouseout with a relatedTarget containment check rather than
	 * mouseenter/mouseleave, which don't bubble and so can't be delegated -
	 * the standard technique for delegating a non-bubbling pair.
	 */
	_bindHoverSlideshow() {
		const SLIDE_INTERVAL_MS = 700;
		const timers = new WeakMap();

		const isWithin = (node, container) => Boolean(node && container && container.contains(node));

		const start = (container) => {
			if (timers.has(container)) return;
			const frames = Array.prototype.slice.call(container.querySelectorAll('.rfhq-pc-img--alt'));
			if (!frames.length) return;

			// index === frames.length is "no alt active" (primary image showing) -
			// one extra step in the cycle so it settles back on the primary
			// between laps rather than jumping straight from the last alt to
			// the first.
			let index = -1;
			const advance = () => {
				index = (index + 1) % (frames.length + 1);
				frames.forEach((frame, i) => frame.classList.toggle('rfhq-pc-img--active', i === index));
			};
			advance();
			timers.set(container, setInterval(advance, SLIDE_INTERVAL_MS));
		};

		const stop = (container) => {
			const timer = timers.get(container);
			if (!timer) return;
			clearInterval(timer);
			timers.delete(container);
			container.querySelectorAll('.rfhq-pc-img--alt').forEach((frame) => {
				frame.classList.remove('rfhq-pc-img--active');
			});
		};

		document.addEventListener('mouseover', (e) => {
			const container = e.target.closest('.card-img-container');
			if (!container || isWithin(e.relatedTarget, container)) return;
			start(container);
		});
		document.addEventListener('mouseout', (e) => {
			const container = e.target.closest('.card-img-container');
			if (!container || isWithin(e.relatedTarget, container)) return;
			stop(container);
		});

		// Keyboard-focus parity with the plain-CSS :focus-within trigger this
		// replaced.
		document.addEventListener('focusin', (e) => {
			const container = e.target.closest('.card-img-container');
			if (container) start(container);
		});
		document.addEventListener('focusout', (e) => {
			const container = e.target.closest('.card-img-container');
			if (container && !isWithin(e.relatedTarget, container)) stop(container);
		});

		// A card removed mid-hover (filter/sort re-render, infinite-scroll
		// replace) would otherwise leave its interval running forever on a
		// detached node - same cleanup contract image_zoom_hover.js already
		// relies on rfhqDom for.
		if (window.rfhqDom && window.rfhqDom.onRemoved) {
			window.rfhqDom.onRemoved((nodes) => {
				nodes.forEach((node) => {
					if (node.matches && node.matches('.card-img-container')) stop(node);
					if (node.querySelectorAll) {
						Array.prototype.forEach.call(node.querySelectorAll('.card-img-container'), stop);
					}
				});
			});
		}
	}

	destroy() {
		this.paginator.detachScroll();
		stopCountdownTicker();
		if (this._unsubscribe) this._unsubscribe();
		this._modelsById.clear();
	}
}

function cssEscape(value) {
	if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
	return String(value).replace(/["\\]/g, '\\$&');
}
